import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiMapPin,
  FiPhone,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiX,
  FiBox,
  FiUser,
  FiHome,
  FiNavigation,
  FiRotateCcw,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import API, { getSchemaName, getImagenUrl } from 'shared/api';
import { fmtMonto } from 'shared/format';
import MapaUbicacion from 'shared/components/MapaUbicacion';
import socket from 'shared/socket';
import { useAuth } from 'shared/context/AuthContext';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const DeliveryPanel = ({ isReadOnly, sedeFilter, isAdmin, deliveryCedula }) => {
  const [listo, setListo] = useState([]);
  const [enCamino, setEnCamino] = useState([]);
  const [entregadoHoy, setEntregadoHoy] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [detallePedido, setDetallePedido] = useState(null);
  const [mapaExpandido, setMapaExpandido] = useState(false);
  const [filtroSede, setFiltroSede] = useState(sedeFilter || '');
  // Los administradores/supervisores ven todos los delivery por defecto (no su propia
  // cedula, que nunca coincide con un pedido y vaciaria las columnas). El repartidor
  // si arranca filtrado a sus propios pedidos.
  const [filtroDelivery, setFiltroDelivery] = useState(isAdmin ? '' : (deliveryCedula || ''));
  const [deliveries, setDeliveries] = useState([]);
  const [sedes, setSedes] = useState([]);
  const { tieneVisibilidad, isTipoEmpresa } = useAuth();
  const v = (c) => tieneVisibilidad(c);
  const esImportador = isTipoEmpresa('importador');
  const columnsRef = useRef(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const viajeActivo = enCamino.length > 0;

  const cargarPanel = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('schema_name', getSchemaName());
      if (filtroSede) params.append('codigo_sede', filtroSede);
      if (filtroDelivery) params.append('id_delivery', filtroDelivery);
      const res = await API.get(`/pedidos/delivery/panel?${params.toString()}`);
      if (res.data.status === 'success') {
        setListo(res.data.data.listo || []);
        setEnCamino(res.data.data.en_camino || []);
        setEntregadoHoy(res.data.data.entregado_hoy || []);
      }
    } catch (e) {
      console.error('Error cargando panel delivery:', e);
    }
  }, [filtroSede, filtroDelivery]);

  // Recarga con debounce para no saturar el backend con rafagas de eventos socket.
  const reloadTimerRef = useRef(null);
  const recargarDebounced = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => cargarPanel(), 400);
  }, [cargarPanel]);
  useEffect(() => {
    return () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    };
  }, []);

  useEffect(() => {
    cargarPanel();
  }, [cargarPanel]);

  useEffect(() => {
    const handlerPedido = () => recargarDebounced();
    const handlerUbicacion = () => recargarDebounced();
    socket.on('pedido_actualizado', handlerPedido);
    socket.on('ubicacion_delivery', handlerUbicacion);
    return () => {
      socket.off('pedido_actualizado', handlerPedido);
      socket.off('ubicacion_delivery', handlerUbicacion);
    };
  }, [recargarDebounced]);

  useEffect(() => {
    API.get('/usuarios/delivery')
      .then((r) => {
        if (r.data.status === 'success') setDeliveries(r.data.data);
      })
      .catch((e) => console.error('Error cargando delivery:', e?.message || e));
    if (isAdmin) {
      API.get('/usuarios/sedes')
        .then((r) => {
          if (r.data.status === 'success')
            setSedes((r.data.data || []).filter((s) => s.activa !== false));
        })
        .catch((e) => console.error('Error cargando sedes:', e?.message || e));
    }
  }, [isAdmin]);

  const toggleSeleccion = (pedidoId) => {
    if (viajeActivo) return;
    setSeleccionados((prev) =>
      prev.includes(pedidoId) ? prev.filter((id) => id !== pedidoId) : [...prev, pedidoId]
    );
  };

  const salirConPedidos = async () => {
    if (seleccionados.length === 0 || viajeActivo) return;
    try {
      await API.post('/pedidos/delivery/asignar-ruta', {
        schema_name: getSchemaName(),
        pedido_ids: seleccionados,
        id_delivery: deliveryCedula || '0',
      });
      setSeleccionados([]);
      cargarPanel();
    } catch (e) {
      console.error('Error:', e);
    }
  };

  const marcarLlegada = async (pedidoId) => {
    await API.put(`/pedidos/${pedidoId}/marcar-llegada`, { schema_name: getSchemaName() });
    cargarPanel();
  };

  const marcarEntregado = async (pedidoId) => {
    await API.put(`/pedidos/delivery/entregar/${pedidoId}`, { schema_name: getSchemaName() });
    cargarPanel();
  };

  const volverAListo = async (pedidoId) => {
    await API.put(`/pedidos/${pedidoId}/volver-a-listo`, { schema_name: getSchemaName() });
    cargarPanel();
  };

  const formatearHora = (f) => {
    if (!f) return '';
    return new Date(f).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const onMouseDown = useCallback((e) => {
    if (!columnsRef.current) return;
    dragging.current = true;
    startX.current = e.pageX - columnsRef.current.offsetLeft;
    scrollLeft.current = columnsRef.current.scrollLeft;
    columnsRef.current.style.cursor = 'grabbing';
    columnsRef.current.style.userSelect = 'none';
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current || !columnsRef.current) return;
    e.preventDefault();
    const x = e.pageX - columnsRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    columnsRef.current.scrollLeft = scrollLeft.current - walk;
  }, []);

  const onMouseUp = useCallback(() => {
    dragging.current = false;
    if (columnsRef.current) {
      columnsRef.current.style.cursor = '';
      columnsRef.current.style.removeProperty('user-select');
    }
  }, []);

  const onMouseLeave = useCallback(() => {
    if (dragging.current) {
      dragging.current = false;
      if (columnsRef.current) {
        columnsRef.current.style.cursor = '';
        columnsRef.current.style.removeProperty('user-select');
      }
    }
  }, []);

  return (
    <div style={S.container}>
      <style>{`
        @media (max-width: 768px) {
          .delivery-columns { flex-wrap: wrap; }
          .delivery-column { min-width: 48% !important; max-width: 48% !important; flex: 0 0 48% !important; }
          .delivery-detail { width: 100% !important; min-width: 100% !important; }
          .delivery-header { flex-direction: column; gap: 8px; align-items: flex-start; }
          .delivery-footer { flex-wrap: wrap; gap: 8px; }
        }
        @media (max-width: 480px) {
          .delivery-column { min-width: 100% !important; max-width: 100% !important; flex: 0 0 100% !important; }
        }
      `}</style>
      <div className="delivery-header" style={S.header}>
        <h2 style={S.title}>Panel de Delivery</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {isAdmin && (
            <>
              <select
                value={filtroSede}
                onChange={(e) => setFiltroSede(e.target.value)}
                style={S.filterSelect}
              >
                <option value="">Todas las sedes</option>
                {sedes.map((s) => (
                  <option key={s.codigo} value={s.codigo}>
                    {s.nombre}
                  </option>
                ))}
              </select>
              <select
                value={filtroDelivery}
                onChange={(e) => setFiltroDelivery(e.target.value)}
                style={S.filterSelect}
              >
                <option value="">Todos los delivery</option>
                {deliveries.map((d) => (
                  <option key={d.cedula} value={d.cedula}>
                    {d.nombre}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div
          ref={columnsRef}
          className="delivery-columns"
          style={{
            flex: 1,
            display: 'flex',
            gap: '12px',
            padding: '12px',
            overflow: 'auto',
            cursor: 'grab',
            userSelect: 'none',
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
        >
          <Columna
            titulo="Listo"
            color="#1a73e8"
            icono={<FiBox size={16} />}
            pedidos={listo}
            seleccionados={seleccionados}
            onToggle={toggleSeleccion}
            onDetalle={setDetallePedido}
            detallePedido={detallePedido}
            soloLectura={isReadOnly}
            viajeActivo={viajeActivo}
            formatearHora={formatearHora}
          />
          <Columna
            titulo="En Camino"
            color="#00bcd4"
            icono={<FiTruck size={16} />}
            pedidos={enCamino}
            onDetalle={setDetallePedido}
            detallePedido={detallePedido}
            soloLectura={isReadOnly}
            onLlegada={marcarLlegada}
            onEntregar={!isReadOnly ? marcarEntregado : null}
            onVolverAListo={!isReadOnly ? volverAListo : null}
            formatearHora={formatearHora}
          />
          <Columna
            titulo={esImportador ? 'Entregado' : 'Entregado Hoy'}
            color="#34a853"
            icono={<FiCheckCircle size={16} />}
            pedidos={entregadoHoy}
            onDetalle={setDetallePedido}
            detallePedido={detallePedido}
            soloLectura
            soloHistorico
            formatearHora={formatearHora}
          />
        </div>

        {detallePedido && (
          <div className="delivery-detail" style={S.detalle}>
            <button
              onClick={() => {
                setDetallePedido(null);
                setMapaExpandido(false);
              }}
              style={S.closeBtn}
            >
              <FiX size={18} />
            </button>
            <div style={{ padding: '14px' }}>
              <h3 style={{ margin: '0 0 2px', fontSize: '18px', fontWeight: 800, color: 'var(--agx-pri)' }}>
                Pedido #{detallePedido.id}
              </h3>
              {v('vis.pedidos.datos_cliente') && detallePedido.cliente && (
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-text)', marginTop: '2px' }}>
                  {detallePedido.cliente}
                </div>
              )}

              {/* Acciones principales */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                {v('vis.pedidos.datos_cliente') && detallePedido.telefono && (
                  <>
                    <a
                      href={`tel:${detallePedido.telefono}`}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        padding: '11px', borderRadius: '10px', background: 'var(--agx-pri2)', color: '#fff',
                        fontSize: '13px', fontWeight: 700, textDecoration: 'none',
                      }}
                    >
                      <FiPhone size={16} /> Llamar
                    </a>
                    <a
                      href={`https://wa.me/${String(detallePedido.telefono).replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        padding: '11px', borderRadius: '10px', background: '#25D366', color: '#fff',
                        fontSize: '13px', fontWeight: 700, textDecoration: 'none',
                      }}
                    >
                      <FaWhatsapp size={17} /> WhatsApp
                    </a>
                  </>
                )}
              </div>
              {v('vis.pedidos.direccion') &&
                detallePedido.lat_entrega &&
                detallePedido.lng_entrega && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${detallePedido.lat_entrega},${detallePedido.lng_entrega}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      marginTop: '8px', padding: '11px', borderRadius: '10px',
                      background: 'var(--agx-bg2)', border: '1px solid var(--agx-border)',
                      color: 'var(--agx-pri2)', fontSize: '13px', fontWeight: 700, textDecoration: 'none',
                    }}
                  >
                    <FiNavigation size={16} /> Navegar
                  </a>
                )}

              {/* Datos del cliente y dirección */}
              <div style={{ marginTop: '14px', padding: '12px', background: 'var(--agx-bg2)', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {v('vis.pedidos.datos_cliente') && detallePedido.telefono && (
                  <div style={{ fontSize: '13px', color: 'var(--agx-text)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiPhone size={13} color="var(--agx-muted)" /> {detallePedido.telefono}
                  </div>
                )}
                {v('vis.pedidos.direccion') && detallePedido.direccion_entrega && (
                  <div style={{ fontSize: '13px', color: 'var(--agx-text)', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: 1.4 }}>
                    <FiMapPin size={13} color="var(--agx-muted)" style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{detallePedido.direccion_entrega}</span>
                  </div>
                )}
                {v('vis.pedidos.datos_cliente') && detallePedido.sede_nombre && (
                  <div style={{ fontSize: '12px', color: 'var(--agx-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiHome size={13} /> {detallePedido.sede_nombre}
                  </div>
                )}
                {v('vis.pedidos.datos_cliente') && detallePedido.delivery_nombre && (
                  <div style={{ fontSize: '12px', color: 'var(--agx-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiUser size={13} /> {detallePedido.delivery_nombre}
                  </div>
                )}
              </div>

              {v('vis.pedidos.total') && (
                <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--agx-blue-bg)', borderRadius: '10px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>Total</span>
                  <span style={{ fontSize: '18px', fontWeight: 800, color: 'var(--agx-pri2)' }}>
                    ${typeof detallePedido.total === 'number' ? fmtMonto(detallePedido.total) : detallePedido.total}
                  </span>
                </div>
              )}

              {/* Productos con imagen */}
              {detallePedido.productos &&
                (() => {
                  let prods = detallePedido.productos;
                  if (typeof prods === 'string')
                    try {
                      prods = JSON.parse(prods);
                    } catch {
                      prods = [];
                    }
                  if (!Array.isArray(prods) || prods.length === 0) return null;
                  return (
                    <div style={{ marginTop: '14px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--agx-muted)', marginBottom: '8px' }}>
                        Productos ({prods.length})
                      </div>
                      {prods.map((p, i) => {
                        const imgSrc = getImagenUrl(p.imagen_url || p.thumb_url);
                        return (
                          <div
                            key={i}
                            style={{
                              display: 'flex', gap: '10px', padding: '8px 0',
                              borderBottom: '1px solid var(--agx-border)',
                              alignItems: 'flex-start',
                            }}
                          >
                            <div
                              style={{
                                width: 46, height: 46, borderRadius: 10, flexShrink: 0,
                                background: 'var(--agx-bg2)', overflow: 'hidden',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid var(--agx-border)',
                              }}
                            >
                              {imgSrc ? (
                                <img src={imgSrc} alt={p.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              ) : (
                                <FiBox size={20} color="var(--agx-faint)" />
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--agx-text)', wordBreak: 'break-word' }}>
                                  {p.nombre || `Item ${i + 1}`}
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--agx-pri2)', flexShrink: 0 }}>
                                  ${typeof p.precio === 'number'
                                    ? fmtMonto(p.pesable ? (p.precio * p.cantidad) / 1000 : p.precio * p.cantidad)
                                    : '0.00'}
                                </span>
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--agx-faint)', marginTop: '1px' }}>
                                x{p.cantidad}
                              </div>
                              {Array.isArray(p.etiquetas) && p.etiquetas.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                  {p.etiquetas.map((et, ei) => (
                                    <span key={ei} style={{ fontSize: '10px', fontWeight: 600, background: 'var(--agx-amber-bg)', color: 'var(--agx-warning-text)', padding: '1px 6px', borderRadius: 4 }}>
                                      {et.nombre}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {p.combo_id && Array.isArray(p.componentes) && p.componentes.length > 0 && (
                                <div style={{ marginTop: '4px', paddingLeft: '4px' }}>
                                  {p.componentes.map((comp, ci) => {
                                    const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
                                    return (
                                      <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--agx-muted)', padding: '1px 0' }}>
                                        <span style={{ display: 'inline-block', width: 4, height: 4, borderRadius: '50%', backgroundColor: '#f0b400', flexShrink: 0 }} />
                                        <span>{comp.nombre}</span>
                                        {compEtiq.length > 0 && (
                                          <span style={{ color: 'var(--agx-faint)' }}>({compEtiq.map((e) => e.nombre).join(', ')})</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

              {v('vis.pedidos.direccion') &&
                detallePedido.lat_entrega &&
                detallePedido.lng_entrega && (
                  <div style={{ marginTop: '14px', cursor: 'pointer' }}>
                    <div onClick={() => setMapaExpandido(true)}>
                      <div style={{ fontSize: '12px', color: 'var(--agx-pri2)', marginBottom: '4px' }}>
                        Ver ubicación →
                      </div>
                      <MapaUbicacion
                        lat={detallePedido.lat_entrega}
                        lng={detallePedido.lng_entrega}
                        readOnly={true}
                        height="140px"
                      />
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      {!isReadOnly && !viajeActivo && seleccionados.length > 0 && (
        <div className="delivery-footer" style={S.footer}>
          <span style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>
            {seleccionados.length} pedidos seleccionados
          </span>
          <button onClick={salirConPedidos} style={S.salirBtn}>
            <FiTruck size={16} /> Salir con pedidos
          </button>
        </div>
      )}

      {viajeActivo && (
        <div
          className="delivery-footer"
          style={{ ...S.footer, backgroundColor: 'var(--agx-blue-bg)' }}
        >
          <span style={{ fontSize: '13px', color: 'var(--agx-pri2)', fontWeight: 600 }}>
            Viaje en curso — {enCamino.length} pedidos pendientes
          </span>
        </div>
      )}

      {mapaExpandido && detallePedido && (
        <div
          onClick={() => setMapaExpandido(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', height: '85vh' }}>
            <MapaUbicacion
              lat={detallePedido.lat_entrega}
              lng={detallePedido.lng_entrega}
              readOnly={true}
              height="85vh"
            />
          </div>
          <div style={{ position: 'absolute', bottom: '60px', display: 'flex', gap: '10px' }}>
            <a
              href={`https://www.google.com/maps?q=${detallePedido.lat_entrega},${detallePedido.lng_entrega}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                padding: '10px 20px',
                backgroundColor: 'var(--agx-pri2)',
                color: '#fff',
                borderRadius: '24px',
                fontSize: '14px',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              }}
            >
              <FiMapPin size={16} /> Abrir en Google Maps
            </a>
          </div>
          {v('vis.pedidos.direccion') && (
            <div
              style={{
                position: 'absolute',
                bottom: '20px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: 'rgba(0,0,0,0.6)',
                padding: '8px 16px',
                borderRadius: '20px',
              }}
            >
              {detallePedido.cliente} · {detallePedido.direccion_entrega || 'Sin dirección'}
            </div>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMapaExpandido(false);
            }}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              fontSize: '20px',
            }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

const Columna = ({
  titulo,
  color,
  icono,
  pedidos,
  seleccionados = [],
  onToggle,
  onDetalle,
  detallePedido,
  soloLectura,
  soloHistorico,
  onLlegada,
  onEntregar,
  onVolverAListo,
  formatearHora,
  viajeActivo,
  onMarcarListo,
}) => {
  const { tieneVisibilidad } = useAuth();
  const v = (c) => tieneVisibilidad(c);

  const tiempoDesde = (f) => {
    if (!f) return '';
    const min = Math.max(0, Math.round((Date.now() - new Date(f).getTime()) / 60000));
    if (min < 1) return 'ahora';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    return `${h} h`;
  };

  return (
    <div
      className="delivery-column"
      style={{
        minWidth: '270px',
        maxWidth: '360px',
        backgroundColor: 'var(--agx-bg)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: isDark() ? color + '30' : color + '15',
          borderRadius: '12px 12px 0 0',
          borderBottom: '2px solid ' + color,
        }}
      >
        <span
          style={{
            fontSize: '13px',
            fontWeight: 700,
            color,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {icono} {titulo}
        </span>
        <span
          style={{
            fontSize: '11px',
            backgroundColor: isDark() ? color + '40' : color + '25',
            color: isDark() ? '#e2e8f0' : color,
            padding: '2px 8px',
            borderRadius: '10px',
            fontWeight: 700,
          }}
        >
          {pedidos.length}
        </span>
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {pedidos.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              color: 'var(--agx-faint)',
              fontSize: '12px',
              padding: '24px 12px',
            }}
          >
            Sin pedidos
          </div>
        )}
        {pedidos.map((p) => {
          const esActual = detallePedido?.id === p.id;
          const esLlegada = p.estado === 'llegada';
          const itemsCount = Array.isArray(p.productos)
            ? p.productos.reduce((s, it) => s + (Number(it.cantidad) || 1), 0)
            : 0;
          return (
            <div
              key={p.id}
              onClick={() => onDetalle(p)}
              style={{
                backgroundColor: esActual
                  ? isDark() ? '#1e3a5f' : '#E8F0FE'
                  : 'var(--agx-surface)',
                borderRadius: '12px',
                padding: '12px',
                border: '1px solid ' + (esActual ? color : 'var(--agx-border)'),
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                boxShadow: esActual ? '0 2px 10px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--agx-pri)' }}>
                      #{p.id}
                    </span>
                    {!soloHistorico && (
                      <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: 6, backgroundColor: color + '22', color }}>
                        {tiempoDesde(p.fechaactualizacion || p.fechapedido)}
                      </span>
                    )}
                  </div>
                  {v('vis.pedidos.datos_cliente') && p.cliente && (
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--agx-text)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p.cliente}
                    </div>
                  )}
                </div>
                {!soloHistorico && !soloLectura && onToggle && (
                  <input
                    type="checkbox"
                    checked={(seleccionados || []).includes(p.id)}
                    disabled={viajeActivo}
                    onChange={(e) => { e.stopPropagation(); onToggle(p.id); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16, flexShrink: 0, accentColor: color }}
                  />
                )}
              </div>

              {v('vis.pedidos.direccion') && p.direccion_entrega && (
                <div style={{ fontSize: '12px', color: 'var(--agx-muted)', display: 'flex', alignItems: 'flex-start', gap: '5px', lineHeight: 1.35 }}>
                  <FiMapPin size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{p.direccion_entrega}</span>
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', alignItems: 'center' }}>
                {v('vis.pedidos.total') && (
                  <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--agx-pri2)' }}>
                    ${typeof p.total === 'number' ? fmtMonto(p.total) : p.total}
                  </span>
                )}
                {itemsCount > 0 && (
                  <span style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>
                    · {itemsCount} item{itemsCount !== 1 ? 's' : ''}
                  </span>
                )}
                <span style={{ fontSize: '11px', color: 'var(--agx-faint)', marginLeft: 'auto' }}>
                  <FiClock size={10} /> {formatearHora(p.fechapedido)}
                </span>
              </div>

              {p.sede_nombre && (
                <div style={{ fontSize: '10px', color: 'var(--agx-faint)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FiHome size={10} /> {p.sede_nombre}
                </div>
              )}

              {!soloLectura && !soloHistorico && (
                <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                  {v('vis.pedidos.datos_cliente') && p.telefono && (
                    <a
                      href={`tel:${p.telefono}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: 'var(--agx-bg2)', color: 'var(--agx-pri2)',
                        fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                        textDecoration: 'none', fontFamily: 'inherit',
                      }}
                    >
                      <FiPhone size={14} /> Llamar
                    </a>
                  )}
                  {v('vis.pedidos.datos_cliente') && p.telefono && (
                    <a
                      href={`https://wa.me/${String(p.telefono).replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: '#25D366', color: '#fff',
                        fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                        textDecoration: 'none', fontFamily: 'inherit', flexShrink: 0,
                      }}
                    >
                      <FaWhatsapp size={15} />
                    </a>
                  )}
                  {onLlegada && !esLlegada && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onLlegada(p.id); }}
                      style={{
                        flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        backgroundColor: '#00bcd4', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Llegué
                    </button>
                  )}
                  {onLlegada && esLlegada && (
                    <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px 12px', fontSize: '11px', color: '#009688', fontWeight: 700 }}>
                      Esperando al cliente...
                    </span>
                  )}
                  {onEntregar && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onEntregar(p.id); }}
                      style={{
                        flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        backgroundColor: '#34a853', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      <FiCheckCircle size={14} /> Entregar
                    </button>
                  )}
                  {onVolverAListo && !esLlegada && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onVolverAListo(p.id); }}
                      style={{
                        flexBasis: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                        padding: '8px 12px', borderRadius: '8px', border: '1px solid #ff6d00',
                        backgroundColor: 'transparent', color: '#ff6d00', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      <FiRotateCcw size={14} /> Volver a listo
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const S = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'var(--agx-bg)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'var(--agx-surface)',
    borderBottom: '1px solid var(--agx-border)',
    flexShrink: 0,
  },
  title: { fontSize: '18px', fontWeight: 700, color: 'var(--agx-pri)', margin: 0 },
  filterSelect: {
    padding: '6px 10px',
    borderRadius: '8px',
    border: '1px solid var(--agx-border)',
    fontSize: '12px',
    fontFamily: 'inherit',
    outline: 'none',
    backgroundColor: 'var(--agx-surface)',
    color: 'var(--agx-text)',
  },
  detalle: {
    width: '320px',
    minWidth: '280px',
    backgroundColor: 'var(--agx-surface)',
    borderLeft: '1px solid var(--agx-border)',
    overflow: 'auto',
    flexShrink: 0,
  },
  closeBtn: {
    padding: '10px 14px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--agx-muted)',
    display: 'flex',
    borderBottom: '1px solid var(--agx-border)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    backgroundColor: 'var(--agx-surface)',
    borderTop: '1px solid var(--agx-border)',
    flexShrink: 0,
  },
  salirBtn: {
    padding: '10px 24px',
    backgroundColor: 'var(--agx-pri2)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};

export default DeliveryPanel;
